import { observer } from 'mobx-react-lite'
import { FormColumn, FormColumns, FormRow } from '@/components/Form'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import {
  JobProgressionBilling,
  ManageExtensionBillingInfo,
} from '@/features/inventory/admin/components/InventoryItemForm/ManageExtensionBillingInfo'
import { type ExtensionBillingViewModel } from '@/features/inventory/admin/inventory-item-dialog/ExtensionBillingViewModel'
import { type ProgressionEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/ProgressionEditorViewModel'
import { type RegularEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/RegularEditorViewModel'

export interface ManageExtensionBillingInfoContainerProps {
  billingVm: ExtensionBillingViewModel
  editing?: boolean
  overridden?: boolean
  testId?: string
}

export const ManageExtensionBillingInfoContainer = observer(
  function ManageExtensionBillingInfoContainer({
    editing,
    billingVm,
    overridden,
    testId,
  }: ManageExtensionBillingInfoContainerProps) {
    return (
      <ManageExtensionBillingInfo
        extensionType={billingVm.extensionType}
        onExtensionTypeChange={billingVm.setExtensionType}
        billingType={billingVm.billingType}
        onBillingTypeChange={billingVm.setExtensionBillingType}
        isJobProgressionBillingEnabled={
          billingVm.enableExtensionJobProgressionBillingInfo
        }
        onJobProgressionBillingEnabledChange={
          billingVm.setExtensionJobProgressionBillingInfo
        }
        editing={editing}
        overridden={overridden}
        testId="inventory-item"
      >
        {billingVm.enableExtensionJobProgressionBillingInfo ? (
          <JobProgressionBillingContainer vm={billingVm.progressionBillingVm} />
        ) : (
          <ExtensionPriceInputsContainer
            vm={billingVm.regularBillingVm}
            testId={testId}
          />
        )}
      </ManageExtensionBillingInfo>
    )
  },
)

export interface ExtensionPriceInputsContainerProps {
  vm: RegularEditorViewModel
  testId?: string
}

export const ExtensionPriceInputsContainer = observer(
  function ExtensionPriceInputsContainer({
    vm,
    testId,
  }: ExtensionPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={vm.isFlat ? 2 : 0}
              label={`Client ${vm.isFlat ? 'amount ($)' : 'percentage (%)'}`}
              field="clientAmount"
              model={vm}
              error={vm.validation.getError('clientAmount')}
              testId={testId && `${testId}-price-input`}
              help={` ${vm.isFlat ? 'The amount to charge the client' : 'The percentage to charge the client based on the remaining total amount on the job'}`}
            />
          </FormColumn>

          <FormColumn>
            <NumericInputContainer
              precision={vm.isFlat ? 2 : 0}
              label={`Pro payout ${vm.isFlat ? 'amount ($)' : 'percentage (%)'}`}
              model={vm}
              field="proPayoutAmount"
              error={vm.validation.getError('proPayoutAmount')}
              testId={testId && `${testId}-pro-price-input`}
              help={` ${vm.isFlat ? 'The amount to pay the pro' : 'The percentage to pay the pro based on the remaining pro payout amount on the job'}`}
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)

interface JobProgressionBillingContainerProps {
  vm: ProgressionEditorViewModel
}

const JobProgressionBillingContainer = observer(
  function JobProgressionBillingContainer({
    vm,
  }: JobProgressionBillingContainerProps) {
    return (
      <JobProgressionBilling
        selectedCount={vm.selectedProgressions.size}
        canAddProgression={vm.canAddProgression}
        onAddProgression={vm.modalVm.show}
        progressions={vm.selectedProgressionsList.map((progression) => ({
          id: progression.value,
          name: progression.name,
          stepNames: progression.steps.map((step) => step.name),
          onEdit: () => vm.editProgression(progression),
          onRemove: () => vm.removeProgression(progression.value),
        }))}
      />
    )
  },
)
